import type { Control, FieldPath, FieldValues } from "react-hook-form";

import { format } from "date-fns";
import { ArrowLeft, CalendarIcon, Check, Loader2, MapPin, Minus, Plus, Share2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import type { State } from "@/api/util";
import type { Contract } from "@/types/contract";

import { usePrivateAxios } from "@/api/use-private-axios";
import { useStates } from "@/api/util";
import ContractDialog from "@/components/dashboard/contract-dialog";
import SearchDropdown from "@/components/dashboard/search-dropdown";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn, formatCurrency } from "@/lib/utils";
import useAuthStore from "@/store/auth";
import { useContractCartStore } from "@/store/contract-cart";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/vendor/contract/cart")({
	component: RouteComponent
});

function RouteComponent() {
	const cart = useContractCartStore(state => state.items);
	const manageItems = useContractCartStore(state => state.manageItems);
	const resetCart = useContractCartStore(state => state.resetCart);
	const removeItem = useContractCartStore(state => state.removeItem);
	const getTotalItems = useContractCartStore(state => state.getTotalItems);

	const totalItems = getTotalItems(cart);

	return (
		<section className="space-y-2">
			<div className="flex gap-2">
				<Button variant="secondary" size="icon" className="mr-auto">
					<ArrowLeft className="h-4 w-4" />
				</Button>
				<Button variant="accent" disabled={totalItems <= 0} onClick={resetCart}>
					Clear Cart
				</Button>
				<CreateContractDialog />
			</div>
			{totalItems > 0
				? (
						<ul className="space-y-4">
							{cart.map(item => (
								<Card key={item.product.id} className="flex flex-wrap shadow-none items-center">
									<CardHeader>
										<div className="size-24 bg-red-300">
											<img
												src={item.product.photos[0]}
												alt={item.product.name}
												className="object-cover w-full h-full"
											/>

										</div>
									</CardHeader>
									<CardContent className="flex flex-col h-full p-0">
										<CardTitle className="capitalize">{item.product.name}</CardTitle>
										<p>
											Price:
											{formatCurrency(Number(item.product.price), "NGN")}
										</p>
										<Button variant="link" className="justify-start px-0 bg-red-5 m-0" asChild>
											<Link to="/vendor/store/$productid" params={{ productid: item.product.id }}>See product details</Link>
										</Button>
									</CardContent>
									<CardFooter className="flex-col ml-auto mt-auto gap-4 items-end ">
										<QuantitySelector
											max={10}
											quantity={item.quantity}
											increase={() => manageItems(item.product, "add")}
											decrease={() => manageItems(item.product, "reduce")}
										/>
										<div className="flex space-x-2">
											<Button className="w-32">Edit</Button>
											<Button className="w-32" variant="accent" onClick={() => removeItem(item.product.id)}>Remove</Button>
										</div>
									</CardFooter>
								</Card>
							))}
						</ul>
					)
				: (
						<div className="bg-white min-h-[80vh] flex flex-col items-center justify-center space-y-4 text-center rounded-lg">
							<p>No Products Provided</p>
							<Button variant="accent" asChild>
								<Link to="/vendor/contract/create">
									Add Products
								</Link>
							</Button>
						</div>
					)}
		</section>
	);
}

interface QuantitySelectorProps {
	quantity?: number;
	min?: number;
	max?: number;
	increase: () => void;
	decrease: () => void;
}

function QuantitySelector({

	quantity = 1,
	min = 0,
	max = 99,
	increase,
	decrease
}: QuantitySelectorProps) {
	const increment = () => {
		if (quantity < max)
			increase();
	};

	const decrement = () => {
		if (quantity > min)
			decrease();
	};

	return (
		<div className="inline-flex items-center rounded-lg border bg-background">
			<Button
				variant="ghost"
				size="icon"
				onClick={decrement}
				disabled={quantity <= min}
				className="h-10 w-10 rounded-l-lg border-r text-indigo-600 hover:bg-indigo-50 hover:text-indigo-600"
			>
				<Minus className="h-4 w-4" />
			</Button>
			<div className="flex h-10 w-12 items-center justify-center text-center">
				<span className="text-sm font-medium">{quantity}</span>
			</div>
			<Button
				variant="ghost"
				size="icon"
				onClick={increment}
				disabled={quantity >= max}
				className="h-10 w-10 rounded-r-lg border-l text-indigo-600 hover:bg-indigo-50 hover:text-indigo-600"
			>
				<Plus className="h-4 w-4" />
			</Button>
		</div>
	);
}

const createProductSchema = z.object({
	title: z
		.string({
			required_error: "Title is required"
		})
		.min(3, {
			message: "Title must be at least 3 characters"
		}),
	weight: z.coerce
		.string({
			required_error: "Weight is required"
		})
		.min(1, { message: "Weight is required" }),
	shippingDate: z.date({
		required_error: "Shipping Date is required"
	}).refine(
		val => val >= new Date(new Date().setHours(0, 0, 0, 0)),
		{ message: "Shipping date cannot be in the past." }
	),
	shipOutState: z.string({
		required_error: "Shipping out state is required"
	}).nonempty(),
	estimatedDeliveryDate: z.date({
		required_error: "Estimated Delivery Date is required"
	}).refine(
		val => val >= new Date(new Date().setHours(0, 0, 0, 0)),
		{ message: "Estimated delivery date cannot be in the past." }
	)

});

interface CreateContractQuery {
	data: Contract;
}
function useCreateContract() {
	const privateAxios = usePrivateAxios();
	const resetCart = useContractCartStore(state => state.resetCart);

	return useMutation({
		mutationKey: ["contracts"],
		mutationFn: async (data: any) => {
			const response = await privateAxios.post<CreateContractQuery>("/contract/create_contract", data, {
				headers: {
					"Content-Type": "multipart/form-data"
				}
			});
			return response.data.data;
		},
		onSuccess() {
			resetCart();
		},
		onError(error) {
			error.message = (error as any).response?.data.message;
			toast.error(error.message);
		}
	});
}

const options = [
	{ value: "5", label: "0 - 10kg" },
	{ value: "15.5", label: "11 - 20kg" },
	{ value: "25.5", label: "21 - 30kg" },
	{ value: "35.5", label: "31 - 40kg" },
	{ value: "45.5", label: "41 - 50kg" },
	{ value: "50", label: "50kg above" }
];

export function CreateContractDialog() {
	const form = useForm<z.infer<typeof createProductSchema>>({
		resolver: zodResolver(createProductSchema),
		defaultValues: {
			title: "",
			weight: "",
			shipOutState: "",
			shippingDate: undefined,
			estimatedDeliveryDate: undefined

		}
	});
	const [open, setOpen] = useState(false);
	const user = useAuthStore(state => state.user);
	const createContract = useCreateContract();
	const cartItems = useContractCartStore(state => state.items);
	const states = useStates(user?.countryId ?? "");

	const product = cartItems.map((item) => {
		return {
			productId: item.product.id,
			productQuantity: item.quantity,
			productSize: item.product.sizes
		};
	});
	const handleSubmit = (data: z.infer<typeof createProductSchema>) => {
		const state = states?.data?.find(state => state.id === data.shipOutState);
		if (!state)
			return (toast.error("Invalid state selected"));
		const form = new FormData();
		form.append("title", String(data.title));
		form.append("estimatedWeight", String(data.weight));
		form.append("type", String("PRODUCT"));
		form.append("shipoutState", state.name);
		form.append("shippingDate", String(data.shippingDate.toISOString()));
		form.append("estimatedDeliveryDate", String(data.estimatedDeliveryDate.toISOString()));

		product.forEach((prod, index) => {
			for (const key in prod) {
				form.append(`product[${index}][${key}]`, String(prod[key as keyof typeof prod]));
			}
		});
		createContract.mutate(form);
	};

	const copyToClipboard = async (text: string) => {
		try {
			await navigator.clipboard.writeText(text);
			toast.success("Text copied to clipboard!");
		}
		catch (error: any) {
			console.log(error);
			toast.error("Failed to copy link");
		}
	};

	const showForm = () => {
		return (
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(handleSubmit)}
					className={cn("bg-white rounded-xl flex flex-col gap-4 flex-wrap h-full")}
				>
					<FormField
						control={form.control}
						name="title"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<Input
										placeholder="Enter contract name"
										{...field}
										className="placeholder-gray-400"
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="shipOutState"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<SearchDropdown
										field={field}
										data={states.data}
										isPending={states.isPending}
										isError={states.isError}
										placeholder="Select a state"
										searchPlaceholder=""
										renderItem={function (item: State): React.ReactNode {
											return (
												<div className="flex items-center gap-3">
													<div className="p-2 bg-gray-100 rounded-lg">
														<MapPin size={20} />
													</div>
													<span className="capitalize">{item.name.toLowerCase()}</span>
												</div>
											);
										}}
										filterFunction={(item: State, query: string) => item.name.toLowerCase().includes(query.toLowerCase())}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="weight"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<Select
										onValueChange={value => field.onChange(value)}
										value={field.value}
									>
										<SelectTrigger>
											<SelectValue placeholder="Select a Estimated weight" />
										</SelectTrigger>
										<SelectContent>
											<SelectGroup>
												{options.map(option => (
													<SelectItem key={option.value} value={option.value}>
														{option.label}
													</SelectItem>
												))}
											</SelectGroup>
										</SelectContent>
									</Select>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<DatePickerInput
						label="Shipping out date"
						name="shippingDate"
						control={form.control}
					/>
					<DatePickerInput
						label="Estimated delivery date"
						name="estimatedDeliveryDate"
						control={form.control}
					/>

				</form>
			</Form>
		);
	};

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button disabled={cartItems.length <= 0}>Continue</Button>
			</DialogTrigger>
			<DialogContent className="space-y-4 sm:max-w-[425px] rounded-lg">
				{!createContract.isSuccess && (
					<DialogHeader>
						<DialogTitle>Generate Contract</DialogTitle>
						<DialogDescription>
							Enter the contract details below
						</DialogDescription>
					</DialogHeader>
				)}
				{createContract.isSuccess
					? (
							<div className="flex w-full items-center justify-center gap-2 flex-col *:text-center">
								<div className="flex items-center justify-center size-32 bg-primary/40 rounded-full">
									<div className="grid place-content-center size-28 bg-primary rounded-full m-auto">
										<Check className="size-12 text-hover-secondary font-semibold text-2xl" />
									</div>
								</div>
								<div>
									<h3>Contract generated</h3>
									<p className="text-muted-foreground text-xs">You have successfully generated a contract for this buyer.</p>
								</div>
								<div className="flex gap-8">
									<div className="flex flex-col items-center gap-2">
										<ContractDialog contract={createContract.data} />
										<p className="text-xs text-muted-foreground">View Contract</p>
									</div>
									<div className="flex flex-col items-center gap-2">
										{/* <Popover open={showShareOptions} onOpenChange={setShowShareOptions}>
											<PopoverTrigger asChild>
												<Button onClick={() => setShowShareOptions(true)} size="icon" className="rounded-full items-center justify-center p-2">
													<Share2 />
												</Button>
											</PopoverTrigger>
											<PopoverContent className="w-80 p-2">
												<div className="space-y-3">
													<button
														onClick={copyToClipboard}
														className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors"
													>
														<div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
															<Copy className="h-5 w-5 text-blue-600" />
														</div>
														<div className="text-left">
															<div className="font-medium text-gray-900">Copy Link</div>
															<div className="text-sm text-gray-500">Copy contract link to clipboard</div>
														</div>
													</button>

													<button
														onClick={shareViaEmail}
														className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors"
													>
														<div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
															<Mail className="h-5 w-5 text-green-600" />
														</div>
														<div className="text-left">
															<div className="font-medium text-gray-900">Email</div>
															<div className="text-sm text-gray-500">Share via email</div>
														</div>
													</button>

													<button
														onClick={shareToWhatsApp}
														className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors"
													>
														<div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
															<MessageCircle className="h-5 w-5 text-green-600" />
														</div>
														<div className="text-left">
															<div className="font-medium text-gray-900">WhatsApp</div>
															<div className="text-sm text-gray-500">Share via WhatsApp</div>
														</div>
													</button>

													<button
														onClick={shareToFacebook}
														className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors"
													>
														<div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
															<Facebook className="h-5 w-5 text-blue-600" />
														</div>
														<div className="text-left">
															<div className="font-medium text-gray-900">Facebook</div>
															<div className="text-sm text-gray-500">Share on Facebook</div>
														</div>
													</button>

													<button
														onClick={shareToInstagram}
														className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors"
													>
														<div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center">
															<Instagram className="h-5 w-5 text-pink-600" />
														</div>
														<div className="text-left">
															<div className="font-medium text-gray-900">Instagram</div>
															<div className="text-sm text-gray-500">Copy text for Instagram story</div>
														</div>
													</button>
												</div>
											</PopoverContent>
										</Popover> */}

										<Button
											onClick={() => {
												if (!createContract.data?.contractCode)
													toast.error("Contract not found");
												copyToClipboard(`Hey! share this code with your buyer to have them pay for their order: ${createContract.data.contractCode}`);
											}}
											size="icon"
											className="rounded-full items-center justify-center p-2"
										>
											<Share2 />
										</Button>
										<p className="text-xs text-muted-foreground">Share Contract</p>
									</div>
								</div>
							</div>
						)
					: showForm()}
				<DialogFooter>
					<div className="flex justify-between w-full gap-4">
						{createContract.isSuccess
							? (
									<Button className="m-auto" asChild>
										<Link to="/vendor">Back to home</Link>
									</Button>
								)
							: (
									<>
										<Button
											variant="secondary"
											className="max-md:w-full"
											disabled={createContract.isPending}
											onClick={() => {
												form.clearErrors();
												setOpen(false);
											}}
										>
											Cancel
										</Button>

										<Button type="button" disabled={createContract.isPending} onClick={form.handleSubmit(handleSubmit)}>
											{createContract.isPending
												? (
														<>
															<Loader2 size={20} className="animate-spin" />
															{" "}
															&nbsp; Loading...
														</>
													)
												: (
														"Continue"
													)}
										</Button>
									</>
								)}
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

interface DatePickerInputProps<T extends FieldValues> {
	label?: string;
	control: Control<T>;
	name: FieldPath<T>;
}

export function DatePickerInput<T extends FieldValues>({ label, control, name }:
DatePickerInputProps<T>) {
	return (

		<FormField
			control={control}
			name={name}
			render={({ field }) => (
				<FormItem>
					<FormControl>
						<Popover>
							<PopoverTrigger asChild>
								<Button
									variant="outline"
									className={cn(
										"w-full justify-start text-left font-normal px-1",
										!field.value && "text-muted-foreground"
									)}
								>
									<CalendarIcon />
									{field.value ? format(field.value, "PPP") : <span>{label}</span>}
								</Button>
							</PopoverTrigger>
							<PopoverContent className="w-auto p-0" align="start">
								<Calendar
									fromDate={new Date()}
									mode="single"
									selected={field.value}
									onSelect={date => field.onChange(date)}
									initialFocus
								/>
							</PopoverContent>
						</Popover>
					</FormControl>
					<FormMessage />
				</FormItem>
			)}
		/>

	);
}
